import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { fetchConversations, saveConversation, deleteConversationApi } from '../api/client';

const SYNC_DEBOUNCE_MS = 900;

function storageKey(userId) {
  return `piua_conversations_${userId}`;
}

function makeId() {
  return `c_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

function makeConversation() {
  return {
    id: makeId(),
    title: 'New chat',
    messages: [],
    updatedAt: Date.now(),
  };
}

/**
 * Manages the user's conversations client-side and keeps them in sync with
 * the server so they can be recovered on another device and exported later.
 *
 * localStorage is used only as an instant-paint cache; the server (once
 * loaded) is the source of truth. Server writes are debounced per-
 * conversation so rapid changes (e.g. a streaming reply) don't spam the API.
 */
export function useConversations(userId) {
  const key = storageKey(userId);
  const [conversations, setConversations] = useState(() => {
    try {
      const raw = localStorage.getItem(key);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) && parsed.length ? parsed : [makeConversation()];
    } catch {
      return [makeConversation()];
    }
  });
  const [activeId, setActiveId] = useState(() => conversations[0]?.id);

  const hydratedRef = useRef(false);
  const syncedAtRef = useRef(new Map());
  const timersRef = useRef(new Map());
  const conversationsRef = useRef(conversations);

  useEffect(() => {
    conversationsRef.current = conversations;
    localStorage.setItem(key, JSON.stringify(conversations));
  }, [key, conversations]);

  // Hydrate from the server once per user. If the server already has
  // conversations, they become the source of truth. Otherwise, if this
  // browser has real (non-empty) local conversations, push them up so
  // they're persisted going forward.
  useEffect(() => {
    hydratedRef.current = false;
    let cancelled = false;

    fetchConversations()
      .then(({ conversations: serverConversations }) => {
        if (cancelled) return;

        if (serverConversations.length) {
          setConversations(serverConversations);
          setActiveId(serverConversations[0].id);
          for (const c of serverConversations) syncedAtRef.current.set(c.id, c.updatedAt);
        } else {
          const local = conversationsRef.current;
          const hasRealContent = local.some((c) => c.messages.length > 0);
          if (hasRealContent) {
            for (const c of local) {
              saveConversation(c.id, c.title, c.messages).catch(() => {});
              syncedAtRef.current.set(c.id, c.updatedAt);
            }
          }
        }
      })
      .catch(() => {
        // Offline or server unavailable — keep working from the local cache.
      })
      .finally(() => {
        if (!cancelled) hydratedRef.current = true;
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // Debounced per-conversation sync to the server whenever a conversation's
  // content actually changes. Skipped while a message is still awaiting its
  // privacy-screened version — so the server (and thus the DB) never
  // receives raw, unfiltered text; it only ever sees what's already been
  // through redaction.
  useEffect(() => {
    if (!hydratedRef.current) return;

    for (const c of conversations) {
      if (syncedAtRef.current.get(c.id) === c.updatedAt) continue;
      if (c.messages.some((m) => m.pendingRedaction)) continue;

      const existingTimer = timersRef.current.get(c.id);
      if (existingTimer) clearTimeout(existingTimer);

      const timer = setTimeout(() => {
        timersRef.current.delete(c.id);
        syncedAtRef.current.set(c.id, c.updatedAt);
        saveConversation(c.id, c.title, c.messages).catch(() => {});
      }, SYNC_DEBOUNCE_MS);

      timersRef.current.set(c.id, timer);
    }
  }, [conversations]);

  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      for (const timer of timers.values()) clearTimeout(timer);
    };
  }, []);

  const activeConversation = useMemo(
    () => conversations.find((c) => c.id === activeId) || conversations[0],
    [conversations, activeId]
  );

  const createConversation = useCallback(() => {
    const conv = makeConversation();
    setConversations((prev) => [conv, ...prev]);
    setActiveId(conv.id);
    return conv.id;
  }, []);

  const deleteConversation = useCallback(
    (id) => {
      const timer = timersRef.current.get(id);
      if (timer) clearTimeout(timer);
      timersRef.current.delete(id);
      syncedAtRef.current.delete(id);
      deleteConversationApi(id).catch(() => {});

      setConversations((prev) => {
        const next = prev.filter((c) => c.id !== id);
        return next.length ? next : [makeConversation()];
      });
    },
    []
  );

  const renameConversation = useCallback((id, title) => {
    setConversations((prev) => prev.map((c) => (c.id === id ? { ...c, title, updatedAt: Date.now() } : c)));
  }, []);

  // Adds a message to a conversation; auto-titles the conversation from the
  // first user message.
  const appendMessage = useCallback((id, message) => {
    setConversations((prev) =>
      prev.map((c) => {
        if (c.id !== id) return c;
        const messages = [...c.messages, message];
        const title =
          c.title === 'New chat' && message.role === 'user'
            ? message.content.slice(0, 48)
            : c.title;
        return { ...c, messages, title, updatedAt: Date.now() };
      })
    );
  }, []);

  // Appends streamed text to the last (assistant) message in a conversation.
  const appendToLastMessage = useCallback((id, textDelta) => {
    setConversations((prev) =>
      prev.map((c) => {
        if (c.id !== id) return c;
        const messages = [...c.messages];
        const last = messages[messages.length - 1];
        if (!last || last.role !== 'assistant') return c;
        messages[messages.length - 1] = { ...last, content: last.content + textDelta };
        return { ...c, messages, updatedAt: Date.now() };
      })
    );
  }, []);

  // Merges fields into the last message in a conversation (e.g. attaching an
  // image once generation completes, rather than appending text).
  const patchLastMessage = useCallback((id, patch) => {
    setConversations((prev) =>
      prev.map((c) => {
        if (c.id !== id) return c;
        const messages = [...c.messages];
        const last = messages[messages.length - 1];
        if (!last) return c;
        messages[messages.length - 1] = { ...last, ...patch };
        return { ...c, messages, updatedAt: Date.now() };
      })
    );
  }, []);

  // Patches the most recent user message — used to swap in the
  // privacy-screened version of what was just sent once the server returns
  // it, so what's displayed (and synced to the server) matches what the
  // model actually saw.
  const patchLastUserMessage = useCallback((id, patch) => {
    setConversations((prev) =>
      prev.map((c) => {
        if (c.id !== id) return c;
        const messages = [...c.messages];
        for (let i = messages.length - 1; i >= 0; i--) {
          if (messages[i].role === 'user') {
            messages[i] = { ...messages[i], ...patch };
            break;
          }
        }
        return { ...c, messages, updatedAt: Date.now() };
      })
    );
  }, []);

  const setMessages = useCallback((id, messages) => {
    setConversations((prev) => prev.map((c) => (c.id === id ? { ...c, messages, updatedAt: Date.now() } : c)));
  }, []);

  return {
    conversations: [...conversations].sort((a, b) => b.updatedAt - a.updatedAt),
    activeId: activeConversation?.id,
    setActiveId,
    activeConversation,
    createConversation,
    deleteConversation,
    renameConversation,
    appendMessage,
    appendToLastMessage,
    patchLastMessage,
    patchLastUserMessage,
    setMessages,
  };
}
